import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { Icon, type IconName } from "./Icon";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as ToastPrimitive from "@radix-ui/react-toast";

const portalContainer = () => document.getElementById("ap-root") ?? document.body;

type ButtonVariant = "default" | "primary" | "ai" | "solid" | "soft" | "surface" | "ghost" | "danger";
const VARIANT_CLASS: Record<ButtonVariant, string> = { default: "surface", primary: "pri", ai: "solid", solid: "solid", soft: "soft", surface: "surface", ghost: "ghost", danger: "dan" };

export function Button({
  variant = "default",
  size,
  icon,
  loading,
  children,
  className = "",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: "sm" | "lg"; icon?: IconName; loading?: boolean }) {
  const cls = ["btn", VARIANT_CLASS[variant], size ?? "", !children && icon ? "icon" : "", className].filter(Boolean).join(" ");
  return (
    <button type="button" {...rest} className={cls} disabled={loading || rest.disabled} aria-busy={loading || undefined}>
      {loading ? <span className="spin" aria-hidden="true" /> : icon ? <Icon name={icon} size="sm" /> : null}
      {children}
    </button>
  );
}

export type Tone = "default" | "ai" | "ok" | "warn" | "bad" | "info" | "note" | "outline";

export function Badge({ tone = "default", variant = "soft", dot, children, title }: { tone?: Tone; variant?: "solid" | "soft" | "surface" | "outline"; dot?: boolean; children: ReactNode; title?: string }) {
  const cls = `badge ${tone === "outline" ? "out" : tone} badge-${variant}`;
  return (
    <span className={cls} title={title}>
      {dot && <span className="d" />}
      {children}
    </span>
  );
}

export function initials(name: string | null | undefined, fallback = "?"): string {
  const parts = (name ?? "").trim().split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  return ((parts[0]![0] ?? "") + (parts.length > 1 ? (parts[parts.length - 1]![0] ?? "") : "")).toUpperCase();
}

export function Avatar({ name, color, size, ai, title }: { name?: string | null; color?: string; size?: "sm" | "lg"; ai?: boolean; title?: string }) {
  if (ai) {
    return (
      <span className={`av aiav${size ? ` ${size}` : ""}`} title={title ?? "Ember"} aria-label="Ember">
        <Icon name="ai" size="sm" />
      </span>
    );
  }
  return (
    <span className={`av${size ? ` ${size}` : ""}`} style={{ background: color ?? "#5B6B8C" }} title={title ?? name ?? undefined}>
      {initials(name)}
    </span>
  );
}

/**
 * Label + control + help/error. Without `htmlFor`, a single child element gets a generated id
 * (unless it already has one) so the label is always associated.
 */
export function Field({ label, help, error, children, htmlFor }: { label?: ReactNode; help?: ReactNode; error?: string | null; children: ReactNode; htmlFor?: string }) {
  const generated = useId();
  let control = children;
  let target = htmlFor;
  const descriptionId = error || help ? `${generated}-description` : undefined;
  if (Children.count(children) === 1 && isValidElement<{ id?: string; "aria-describedby"?: string; "aria-invalid"?: boolean }>(children)) {
    target = htmlFor ?? children.props.id ?? generated;
    control = cloneElement(children, { id: target, "aria-describedby": [children.props["aria-describedby"], descriptionId].filter(Boolean).join(" ") || undefined, "aria-invalid": error ? true : children.props["aria-invalid"] });
  }
  return (
    <div className="field">
      {label && <label htmlFor={target}>{label}</label>}
      {control}
      {error ? <div id={descriptionId} className="err" role="alert">{error}</div> : help ? <div id={descriptionId} className="help">{help}</div> : null}
    </div>
  );
}

export function Input({ invalid, className = "", ...rest }: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return <input className={`inp${invalid ? " bad" : ""} ${className}`} aria-invalid={invalid || undefined} {...rest} />;
}

export function Textarea({ invalid, className = "", ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return <textarea className={`inp${invalid ? " bad" : ""} ${className}`} aria-invalid={invalid || undefined} {...rest} />;
}

export function Select({ className = "", children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={`inp ${className}`} {...rest}>
      {children}
    </select>
  );
}

/** Labelled field wrapper that wires `id` automatically. */
export function TextField({ label, help, error, ...input }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; help?: ReactNode; error?: string | null }) {
  const id = useId();
  return (
    <Field label={label} help={help} error={error} htmlFor={id}>
      <Input id={id} invalid={!!error} {...input} />
    </Field>
  );
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; disabled?: boolean }) {
  const id = useId();
  return <span className="tgl">
    <SwitchPrimitive.Root id={id} checked={checked} onCheckedChange={onChange} disabled={disabled} aria-label={typeof label === "string" ? label : undefined} className="switch">
      <SwitchPrimitive.Thumb className="switch-thumb" />
    </SwitchPrimitive.Root>
    {label && <label htmlFor={id}>{label}</label>}
  </span>;
}

export function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: { value: T; label: ReactNode }[]; onChange: (v: T) => void; label?: string }) {
  return <ToggleGroup.Root type="single" className="seg" value={value} onValueChange={(next) => { if (next) onChange(next as T); }} aria-label={label}>
    {options.map((option) => <ToggleGroup.Item key={option.value} value={option.value} className={option.value === value ? "on" : ""}>{option.label}</ToggleGroup.Item>)}
  </ToggleGroup.Root>;
}

export function Tabs<T extends string>({ value, tabs, onChange }: { value: T; tabs: { value: T; label: ReactNode }[]; onChange: (v: T) => void }) {
  return <TabsPrimitive.Root value={value} onValueChange={(next) => onChange(next as T)}>
    <TabsPrimitive.List className="tabs">
      {tabs.map((tab) => <TabsPrimitive.Trigger key={tab.value} value={tab.value} className={`tab${tab.value === value ? " on" : ""}`}>{tab.label}</TabsPrimitive.Trigger>)}
    </TabsPrimitive.List>
  </TabsPrimitive.Root>;
}

export function Card({ title, description, actions, children, className = "" }: { title?: ReactNode; description?: ReactNode; actions?: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <div className="hstack" style={{ alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {title && <h2>{title}</h2>}
            {description && <p className="cdesc">{description}</p>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function PageHeader({ title, description, actions }: { title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-head">
      <div>
        <h1>{title}</h1>
        {description && <div className="desc">{description}</div>}
      </div>
      {actions && <div className="acts">{actions}</div>}
    </header>
  );
}

export function Table({ children, label }: { children: ReactNode; label: string }) {
  return <div style={{ overflowX: "auto" }} tabIndex={0} role="region" aria-label={label}><table>{children}</table></div>;
}

export function BarChart({ data, label }: { data: { label: string; human: number; ember: number }[]; label: string }) {
  const maximum = Math.max(1, ...data.map((point) => point.human + point.ember));
  return <figure style={{ margin: 0 }}>
    <div className="bars" role="img" aria-label={`${label}: ${data.map((point) => `${point.label}, team ${point.human}, Ember ${point.ember}`).join("; ")}`}>
      {data.map((point, index) => <div key={`${point.label}-${index}`} title={`${point.label}: team ${point.human}, Ember ${point.ember}`}><span className="h" style={{ height: `${point.human / maximum * 100}%` }} /><span className="a" style={{ height: `${point.ember / maximum * 100}%` }} /></div>)}
    </div>
    <figcaption className="leg" style={{ marginTop: 10 }}><span><i style={{ background: "var(--bd2)" }} />Team</span><span><i style={{ background: "var(--acc)" }} />Ember</span></figcaption>
  </figure>;
}

export function Banner({ tone = "default", icon, children, action }: { tone?: "default" | "ai" | "warn" | "bad" | "ok" | "info"; icon?: IconName; children: ReactNode; action?: ReactNode }) {
  return (
    <div className={`banner${tone === "default" ? "" : ` ${tone}`}`} role={tone === "bad" ? "alert" : "status"}>
      {icon && <Icon name={icon} size="sm" />}
      <span className="bt">{children}</span>
      {action}
    </div>
  );
}

export function EmptyState({ icon = "check", title, children, action }: { icon?: IconName; title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="stack" style={{ alignItems: "center", maxWidth: 360 }}>
        <Icon name={icon} />
        <div style={{ fontWeight: 600, color: "var(--tx)" }}>{title}</div>
        {children && <div>{children}</div>}
        {action}
      </div>
    </div>
  );
}

export function ErrorState({ title = "Something went wrong", error, onRetry }: { title?: ReactNode; error?: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : null;
  return (
    <Banner tone="bad" icon="alert" action={onRetry && <Button size="sm" onClick={onRetry}>Retry</Button>}>
      <b>{title}</b>
      {message && <> · {message}</>}
    </Banner>
  );
}

export function Skeleton({ width = "100%", height = 12 }: { width?: number | string; height?: number }) {
  return <div className="sk" style={{ width, height }} aria-hidden="true" />;
}

export function Spinner() {
  return <span className="spin" role="status" aria-label="Loading" />;
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="kbd">{children}</kbd>;
}

// ---------- Dialog ----------

export function Dialog({ open, onClose, title, children, footer, width = 420 }: { open: boolean; onClose: () => void; title: ReactNode; children?: ReactNode; footer?: ReactNode; width?: number }) {
  const returnFocus = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => { if (open) returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; }, [open]);
  return <DialogPrimitive.Root open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
    <DialogPrimitive.Portal container={portalContainer()}>
      <DialogPrimitive.Overlay className="scrim radix-scrim" />
      <DialogPrimitive.Content className="dlg radix-dialog" style={{ maxWidth: width }} aria-describedby={undefined} onCloseAutoFocus={(event) => { event.preventDefault(); returnFocus.current?.focus(); }}>
        <div className="b">
          <div className="dialog-heading"><DialogPrimitive.Title>{title}</DialogPrimitive.Title><DialogPrimitive.Close asChild><Button variant="ghost" icon="x" aria-label="Close dialog" /></DialogPrimitive.Close></div>
          {children}
        </div>
        {footer && <div className="f">{footer}</div>}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  </DialogPrimitive.Root>;
}

export function ConfirmDialog({ open, title, body, confirmLabel = "Confirm", danger, onConfirm, onClose }: { open: boolean; title: string; body: ReactNode; confirmLabel?: string; danger?: boolean; onConfirm: () => Promise<unknown> | void; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => { if (open) returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; }, [open]);
  useEffect(() => { if (open) setError(null); }, [open]);
  return <AlertDialogPrimitive.Root open={open} onOpenChange={(next) => { if (!next && !busy) onClose(); }}>
    <AlertDialogPrimitive.Portal container={portalContainer()}>
      <AlertDialogPrimitive.Overlay className="scrim radix-scrim" />
      <AlertDialogPrimitive.Content className="dlg radix-dialog" onCloseAutoFocus={(event) => { event.preventDefault(); returnFocus.current?.focus(); }}>
        <div className="b">
          <AlertDialogPrimitive.Title>{title}</AlertDialogPrimitive.Title>
          <AlertDialogPrimitive.Description asChild><div className="sub">{body}</div></AlertDialogPrimitive.Description>
          {error && <div className="err" role="alert">{error}</div>}
        </div>
        <div className="f">
          <AlertDialogPrimitive.Cancel asChild><Button disabled={busy}>Cancel</Button></AlertDialogPrimitive.Cancel>
          <Button variant={danger ? "danger" : "primary"} loading={busy} onClick={async () => {
            setBusy(true);
            setError(null);
            try { await onConfirm(); onClose(); }
            catch (cause) { setError(cause instanceof Error ? cause.message : "The action could not be completed. Please try again."); }
            finally { setBusy(false); }
          }}>{confirmLabel}</Button>
        </div>
      </AlertDialogPrimitive.Content>
    </AlertDialogPrimitive.Portal>
  </AlertDialogPrimitive.Root>;
}

export function Menu({ trigger, items, align = "right" }: { trigger: (props: { onClick: () => void; "aria-expanded": boolean }) => ReactNode; items: ({ label: ReactNode; icon?: IconName; onSelect: () => void; danger?: boolean; kbd?: string } | { header: string })[]; align?: "left" | "right" }) {
  const [open, setOpen] = useState(false);
  return <DropdownMenu.Root open={open} onOpenChange={setOpen}>
    <DropdownMenu.Trigger asChild>{trigger({ onClick: () => setOpen(!open), "aria-expanded": open })}</DropdownMenu.Trigger>
    <DropdownMenu.Portal container={portalContainer()}>
      <DropdownMenu.Content className="pop radix-pop" align={align === "left" ? "start" : "end"} sideOffset={5} collisionPadding={8}>
        {items.map((item, index) => "header" in item
          ? <DropdownMenu.Label key={index} className="ph">{item.header}</DropdownMenu.Label>
          : <DropdownMenu.Item key={index} className={`pi${item.danger ? " danger" : ""}`} onSelect={item.onSelect}>
            {item.icon && <Icon name={item.icon} size="sm" />}{item.label}{item.kbd && <Kbd>{item.kbd}</Kbd>}
          </DropdownMenu.Item>)}
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  </DropdownMenu.Root>;
}

export function Tooltip({ children, content }: { children: ReactNode; content: ReactNode }) {
  return <TooltipPrimitive.Provider delayDuration={350}><TooltipPrimitive.Root>
    <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
    <TooltipPrimitive.Portal container={portalContainer()}><TooltipPrimitive.Content className="tipb" sideOffset={6} collisionPadding={8}>{content}<TooltipPrimitive.Arrow className="tooltip-arrow" /></TooltipPrimitive.Content></TooltipPrimitive.Portal>
  </TooltipPrimitive.Root></TooltipPrimitive.Provider>;
}

export function Popover({ trigger, children, open, onOpenChange }: { trigger: ReactNode; children: ReactNode; open?: boolean; onOpenChange?: (open: boolean) => void }) {
  return <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
    <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
    <PopoverPrimitive.Portal container={portalContainer()}><PopoverPrimitive.Content className="pop radix-pop popover-content" sideOffset={6} collisionPadding={8}>{children}</PopoverPrimitive.Content></PopoverPrimitive.Portal>
  </PopoverPrimitive.Root>;
}

export function Checkbox({ checked, onChange, label, disabled }: { checked: boolean | "indeterminate"; onChange: (checked: boolean | "indeterminate") => void; label: ReactNode; disabled?: boolean }) {
  const id = useId();
  return <span className="choice"><CheckboxPrimitive.Root id={id} className="checkbox" checked={checked} onCheckedChange={onChange} disabled={disabled}><CheckboxPrimitive.Indicator><Icon name={checked === "indeterminate" ? "minus" : "check"} size="sm" /></CheckboxPrimitive.Indicator></CheckboxPrimitive.Root><label htmlFor={id}>{label}</label></span>;
}

export function RadioGroup<T extends string>({ value, options, onChange, label }: { value: T; options: { value: T; label: ReactNode; disabled?: boolean }[]; onChange: (value: T) => void; label: string }) {
  const id = useId();
  return <RadioGroupPrimitive.Root className="radio-group" value={value} onValueChange={(next) => onChange(next as T)} aria-label={label}>
    {options.map((option, index) => <span className="choice" key={option.value}><RadioGroupPrimitive.Item className="radio" id={`${id}-${index}`} value={option.value} disabled={option.disabled}><RadioGroupPrimitive.Indicator className="radio-dot" /></RadioGroupPrimitive.Item><label htmlFor={`${id}-${index}`}>{option.label}</label></span>)}
  </RadioGroupPrimitive.Root>;
}

export function CustomSelect({ value, onChange, options, label, placeholder, disabled }: { value?: string; onChange: (value: string) => void; options: { value: string; label: string; disabled?: boolean }[]; label: string; placeholder?: string; disabled?: boolean }) {
  return <SelectPrimitive.Root value={value} onValueChange={onChange} disabled={disabled}>
    <SelectPrimitive.Trigger className="inp select-trigger" aria-label={label}><SelectPrimitive.Value placeholder={placeholder} /><SelectPrimitive.Icon><Icon name="chevronDown" size="sm" /></SelectPrimitive.Icon></SelectPrimitive.Trigger>
    <SelectPrimitive.Portal container={portalContainer()}><SelectPrimitive.Content className="pop radix-pop" position="popper" sideOffset={5}><SelectPrimitive.Viewport>{options.map((option) => <SelectPrimitive.Item className="pi" key={option.value} value={option.value} disabled={option.disabled}><SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText><SelectPrimitive.ItemIndicator><Icon name="check" size="sm" /></SelectPrimitive.ItemIndicator></SelectPrimitive.Item>)}</SelectPrimitive.Viewport></SelectPrimitive.Content></SelectPrimitive.Portal>
  </SelectPrimitive.Root>;
}

// ---------- Toasts ----------

interface ToastItem {
  id: number;
  message: ReactNode;
  tone: "default" | "bad";
  action?: { label: string; onClick: () => void };
}

const ToastContext = createContext<(message: ReactNode, opts?: { tone?: "default" | "bad"; action?: ToastItem["action"] }) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((message: ReactNode, opts: { tone?: "default" | "bad"; action?: ToastItem["action"] } = {}) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs.slice(-2), { id, message, tone: opts.tone ?? "default", action: opts.action }]);
    // Radix owns dismissal timers, including pause on hover and keyboard focus.
  }, []);
  return (
    <ToastContext.Provider value={push}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {items.map((t) => <ToastPrimitive.Root key={t.id} className={`toast${t.tone === "bad" ? " bad" : ""}`} duration={t.tone === "bad" ? 7000 : 4000} onOpenChange={(open) => { if (!open) setItems((xs) => xs.filter((x) => x.id !== t.id)); }}>
          <Icon name={t.tone === "bad" ? "alert" : "check"} size="sm" />
          <ToastPrimitive.Description asChild><span>{t.message}</span></ToastPrimitive.Description>
          {t.action && <ToastPrimitive.Action asChild altText={t.action.label}><button type="button" className="link" onClick={t.action.onClick}>{t.action.label}</button></ToastPrimitive.Action>}
          <ToastPrimitive.Close asChild><Button variant="ghost" size="sm" icon="x" aria-label="Dismiss notification" /></ToastPrimitive.Close>
        </ToastPrimitive.Root>)}
        <ToastPrimitive.Viewport className="toasts" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

/** `toast("Saved")`, `toast("Couldn't reach Stripe", { tone: "bad", action: { label: "Retry", onClick } })`. */
export const useToast = () => useContext(ToastContext);

// ---------- formatting ----------

export function relativeTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "";
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 45) return "now";
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  if (s < 86400 * 7) return `${Math.round(s / 86400)}d`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function clockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export function money(cents: number, currency = "USD"): string {
  return new Intl.NumberFormat(undefined, { style: "currency", currency, minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
}
